/**
 * Topbar — 52px-tall app header with breadcrumbs on the left, an optional
 * search slot, and a notifications/theme/avatar cluster on the right.
 */
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Bell, ChevronRight, Search } from './icons';
import { cn } from './cn';

export interface BreadcrumbItem {
  label: string;
  /** Internal TanStack Router path; omit on the last (current) crumb. */
  to?: string;
}

export interface TopbarProps {
  breadcrumbs?: readonly BreadcrumbItem[];
  /** Optional right-side actions (e.g. theme toggle, user menu). */
  right?: ReactNode;
  /** Show the global ⌘K search input. Defaults to true. */
  showSearch?: boolean;
  /** Initials for the user avatar; pass null/undefined to hide. */
  userInitials?: string | null;
  /** Full name / e-mail of the current user for the avatar tooltip + a11y label. */
  userLabel?: string;
  /** Hide the unread-notifications dot. */
  hasUnread?: boolean;
  className?: string;
}

export function Topbar({
  breadcrumbs = [],
  right,
  showSearch = true,
  // No default initials: the avatar must reflect the actual signed-in user, so
  // a missing value hides it rather than falling back to a hardcoded person.
  userInitials = null,
  userLabel,
  hasUnread = false,
  className,
}: TopbarProps): JSX.Element {
  return (
    <header
      className={cn(
        'flex h-topbar shrink-0 items-center gap-4 border-b border-border-subtle bg-surface px-4',
        className,
      )}
      role="banner"
    >
      <Breadcrumbs items={breadcrumbs} />

      {showSearch ? (
        <div className="max-w-md flex-1">
          <label className="sr-only" htmlFor="topbar-search">
            Zoeken
          </label>
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted"
              aria-hidden="true"
            />
            <input
              id="topbar-search"
              type="search"
              placeholder="Zoek tenant, domein of IP..."
              className="h-7 w-full rounded border border-border-subtle bg-surface-subtle pl-8 pr-10 text-xs text-fg-primary placeholder:text-fg-muted focus:border-border focus:bg-surface focus:outline-none"
            />
            <kbd
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border-subtle bg-surface px-1 font-mono text-xs text-fg-muted"
            >
              ⌘K
            </kbd>
          </div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="flex items-center gap-3 text-fg-muted">
        {right}
        <button
          type="button"
          aria-label="Notificaties"
          className="relative transition hover:text-fg-primary"
        >
          <Bell size={16} aria-hidden="true" />
          {hasUnread ? (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-fail-600 ring-2 ring-[var(--color-surface-default)]"
            />
          ) : null}
        </button>
        {userInitials ? (
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-fg-secondary"
            aria-label={userLabel ? `Aangemeld als ${userLabel}` : 'Huidige gebruiker'}
            title={userLabel ?? 'Huidige gebruiker'}
          >
            {userInitials}
          </div>
        ) : null}
      </div>
    </header>
  );
}

function Breadcrumbs({ items }: { items: readonly BreadcrumbItem[] }): JSX.Element {
  return (
    <nav aria-label="Broodkruimels" className="flex items-center gap-1.5 text-sm text-fg-muted">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} className="inline-flex items-center gap-1.5">
            {i > 0 ? (
              <ChevronRight size={12} className="text-fg-disabled" aria-hidden="true" />
            ) : null}
            {item.to && !isLast ? (
              <Link to={item.to} className="transition-colors hover:text-fg-primary">
                {item.label}
              </Link>
            ) : (
              <span
                className={cn(isLast ? 'font-medium text-fg-primary' : '')}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
